import { parseMoneyToCents } from '@agos/shared'

import type { CartLine } from './cart'

/**
 * *Buy two pizzas, have a third on us* — the rules behind the gift offer.
 *
 * **Measured: one rule is active on this data** — `categoryKeyword: "pizza"`, `minCount: 2`,
 * `giftName: "Šunková Pizza Darček"`, `giftPrice: 2`, `multiply: true`. So a customer with two pizzas in
 * the basket is owed a gift right now and this port had no way to offer it: `giftRuleId` was in
 * `CartLine` and in the order payload with no UI anywhere, which is FB-C24's shape again.
 *
 * **The server decides whether it was really earned.** `GiftRules.stripUnearnedGifts` drops the line
 * when the qualifying count is not met, so everything here is a claim the basket makes and the server
 * checks — which is the right way round, and why none of this arithmetic is a security boundary.
 *
 * Three counting rules are legacy's and each one matters:
 *
 * - **A gift never earns a gift.** A line already carrying `giftRuleId` is excluded from the count, or
 *   two pizzas would earn a third, which would earn a fourth.
 * - **A daily-menu dish never counts.** It is priced by its own rules and belongs to a day.
 * - **`multiply` decides whether four pizzas earn one gift or two.** The measured rule multiplies.
 */

export interface GiftRule {
  id: number
  categoryKeyword: string
  minCount: number
  giftName: string
  giftPriceCents: number
  multiply: boolean
  active: boolean
}

export function giftRulesOf(payload: unknown): GiftRule[] {
  if (!Array.isArray(payload)) return []
  const rules: GiftRule[] = []
  for (const raw of payload) {
    if (typeof raw !== 'object' || raw === null) continue
    const row = raw as Record<string, unknown>
    const minCount = row.minCount
    const keyword = typeof row.categoryKeyword === 'string' ? row.categoryKeyword.trim().toLowerCase() : ''
    const name = typeof row.giftName === 'string' ? row.giftName.trim() : ''
    // A rule with no keyword matches nothing and a rule with no name cannot be offered; both would be
    // an invisible entry in a list the operator thinks is working.
    if (typeof row.id !== 'number' || keyword === '' || name === '') continue
    if (typeof minCount !== 'number' || !Number.isInteger(minCount) || minCount < 1) continue
    rules.push({
      id: row.id,
      categoryKeyword: keyword,
      minCount,
      giftName: name,
      giftPriceCents:
        typeof row.giftPrice === 'number' || typeof row.giftPrice === 'string'
          ? parseMoneyToCents(row.giftPrice)
          : 0,
      multiply: row.multiply === true,
      active: row.active === true,
    })
  }
  return rules
}

export interface GiftOffer {
  ruleId: number
  giftName: string
  giftPriceCents: number
  /** How many are owed and not yet in the basket. */
  count: number
}

/** A basket line's category name, for matching against a rule's keyword. */
export interface CountableLine {
  line: CartLine
  categoryName: string
}

/**
 * What the basket has earned and not yet taken.
 *
 * `count` is what is still owed — entitled minus what is already in the basket — so an offer that has
 * been accepted stops being made, and a customer who adds two more pizzas is offered another.
 */
export function giftOffers(rules: readonly GiftRule[], lines: readonly CountableLine[]): GiftOffer[] {
  const offers: GiftOffer[] = []
  for (const rule of rules) {
    if (!rule.active) continue

    const qualifying = lines.reduce((total, entry) => {
      // A gift never earns a gift, and a daily-menu dish never counts towards one.
      if (entry.line.giftRuleId !== undefined) return total
      if (entry.line.productId < 0 && entry.line.composed === undefined) return total
      // An assembled line — a kombo — counts: it is pizzas, however it was put together.
      const matches =
        entry.line.composed !== undefined
          ? entry.line.composed.name.toLowerCase().includes(rule.categoryKeyword)
          : entry.categoryName.toLowerCase().includes(rule.categoryKeyword)
      return matches ? total + entry.line.quantity : total
    }, 0)

    const held = lines
      .filter((entry) => entry.line.giftRuleId === rule.id)
      .reduce((total, entry) => total + entry.line.quantity, 0)

    const entitled = rule.multiply
      ? Math.floor(qualifying / rule.minCount)
      : qualifying >= rule.minCount
        ? 1
        : 0
    const owed = Math.max(0, entitled - held)
    if (owed > 0) {
      offers.push({ ruleId: rule.id, giftName: rule.giftName, giftPriceCents: rule.giftPriceCents, count: owed })
    }
  }
  return offers
}

/**
 * The basket line a gift becomes.
 *
 * `productId: 0` and the rule id beside it — the server matches on `giftRuleId`, and `lineKey` already
 * keeps a gift apart from a bought one of the same thing. Merging them would either give away the paid
 * item or charge for the gift, depending which line won.
 *
 * **The price is the rule's, not zero — §10A.109, the owner's Q2.** *"darček šunková pizza za 3 eurá
 * k objednávke, but when I added it in my košík, I see it as 0,00 euro."* This carried
 * `unitPriceCents: 0`, so the basket showed a free pizza; legacy puts `offer.giftPrice` on the line
 * (`Home.tsx:612`) and sums it like any other. A `product_gift_rules` row is a named item at a
 * **reduced** price - the column is called `gift_price` - and the owner's own rule is 2,00 €.
 *
 * The figure here is what the basket *shows*; the server re-reads it from the rule and is what the
 * customer is *charged*. They have to agree, which is why this changed with §10A.109 rather than
 * after it.
 */
export function giftLine(offer: GiftOffer): CartLine {
  return {
    productId: 0,
    extras: '',
    quantity: offer.count,
    giftRuleId: offer.ruleId,
    composed: { name: offer.giftName, unitPriceCents: offer.giftPriceCents },
  }
}
